import React, { useCallback, useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridSortItem,
  GridRowModesModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { Container } from "@mui/material";
import { useSnackbar } from "notistack";
import { AccountExtended } from "../../types/account";
import { AccountsExtended } from "../../api/webmaps";
import BooleanValue from "../BooleanValue";
import { Account } from "../../api/portal";
import { makeColumn } from "../../util/tableUtils";

const columns: GridColDef[] = [
  { field: "id", headerName: "Account", type: "number", width: 100 },
  {
    field: "admin",
    headerName: "Admin",
    type: "boolean",
    width: 65,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.admin} />
    ),
  },
  { field: "kontakt_id", headerName: "Kontakt", width: 75 },
  { field: "kunden_id", headerName: "Kunde", width: 75 },
  {
    field: "intern",
    headerName: "intern",
    type: "boolean",
    width: 70,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.intern} />
    ),
  },
  {
    field: "cookies_1",
    headerName: "Cookies (1)",
    type: "boolean",
    width: 90,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.cookies_1} />
    ),
  },
  {
    field: "cookies_2",
    headerName: "Cookies (2)",
    type: "boolean",
    width: 90,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.cookies_2} />
    ),
  },
  {
    field: "dse",
    headerName: "DSE",
    type: "boolean",
    width: 60,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.dse} />
    ),
  },
  {
    field: "vnb",
    headerName: "VNB",
    type: "boolean",
    width: 60,
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row.vnb} />
    ),
  },
  { field: "vorname", headerName: "Vorname", width: 100 },
  { field: "nachname", headerName: "Nachname", width: 100 },
  { field: "position", headerName: "Position", width: 150 },
  { field: "institut", headerName: "Institut", width: 275 },
  { field: "stn", headerName: "Straße", width: 150 },
  { field: "hnr", headerName: "Hnr.", type: "number", width: 60 },
  { field: "hnrz", headerName: "Hnrz.", width: 60 },
  {
    field: "faktura",
    headerName: "Faktura",
    type: "boolean",
    renderCell: (params: GridRenderCellParams<Account>) => (
      <BooleanValue value={params.row?.faktura} />
    ),
  },
  { field: "plz", headerName: "PLZ" },
  { field: "ort", headerName: "Ort" },
  { field: "ortz", headerName: "Ortsz." },
  { field: "username", headerName: "Username", width: 190 },
  { field: "email", headerName: "Email", width: 230 },
  { field: "mobil", headerName: "Mobil", width: 200 },
  { field: "telefon", headerName: "Telefon", width: 200 },
  { field: "aktiv", headerName: "aktiv", type: "boolean", width: 70 },
  { field: "is_removed", headerName: "gelöscht", type: "boolean", width: 75 },
  { field: "rolle", headerName: "Rolle", type: "string", width: 70 },
  { field: "sec_level", headerName: "Sec Level", type: "number", width: 75 },
];

const AccountsExtendedTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [orderByModel, setOrderByModel] = useState<GridSortItem[]>([
    {
      field: "id",
      sort: "asc",
    },
  ]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [accountsData, setAccountsData] = useState<
    Omit<AccountExtended, "changed_by" | "created_by">[]
  >([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleUpdate = (
    row: Omit<AccountExtended, "changed_by" | "created_by">
  ) => {
    setIsLoading(true);
    AccountsExtended.update(row.id, row) // update row data
      .then(() => {
        enqueueSnackbar(`Account ${row.id} aktualisiert`, {
          key: `row-updated-${row.id}`,
        });
        setIsLoading(false);
      });
  };

  const fetchWebmaps = useCallback(() => {
    const { page, pageSize } = paginationModel;
    AccountsExtended.getAll(
      orderByModel[0]?.field, // community edition only supports single sort
      orderByModel[0]?.sort,
      pageSize,
      page * pageSize
    )
      .then((res) => {
        // we use the x-total-count header for retrieving the total number of resources
        setTotalRowCount(Number(res.headers["x-total-count"]));
        return res.data;
      })
      .then((accounts: AccountExtended[]) => {
        setAccountsData(accounts);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [orderByModel, paginationModel]);

  useEffect(() => {
    setIsLoading(true);
    fetchWebmaps();
  }, [orderByModel, paginationModel, fetchWebmaps]);

  return (
    <Container
      style={{ width: "100%" }}
      sx={{ m: 0, p: 0, height: "calc(100vh - 157px)" }}
      maxWidth={false}
      disableGutters
    >
      <DataGrid
        rows={accountsData}
        columns={columns.map((col) => {
          return makeColumn(col);
        })}
        rowModesModel={rowModesModel}
        onRowModesModelChange={(model) => {
          setRowModesModel(model);
        }}
        pageSizeOptions={[10, 20, 50]}
        editMode="row"
        processRowUpdate={(
          row: Omit<AccountExtended, "changed_by" | "created_by">
        ) => {
          handleUpdate(row);
          return row;
        }}
        onProcessRowUpdateError={() => {}}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
        }}
        sortingMode="server"
        sortModel={orderByModel}
        onSortModelChange={(model: GridSortModel) => {
          setOrderByModel(model);
        }}
        paginationMode="server"
        rowCount={totalRowCount}
        loading={isLoading}
      />
    </Container>
  );
};

export default AccountsExtendedTable;
